import { logger } from "@/utils/logger";
import { MessagePart } from '../types';
import { hasVehicleProperties } from '../vehicleDetection';
import { parseReplyMessage } from '../replyMessageParser';
import { storeInterestData } from '../../interestDataUtils';
import { addCarfaxRequest, shouldShowCarfaxForm, shouldShowCarfaxReport, updateCarfaxSent } from '../../carfaxTrackingUtils';
import { securedSupabase } from '../../../integrations/supabase/secured-client';
import { getDealerID } from '../../dealerDataUtils';
import { sessionManager } from '../../sessionManager';
import { processInventoryHydration } from '../../vehicleHydration';

// Extend Window interface for debounce tracking
declare global {
  interface Window {
    _vehicleInterestDebounce?: { [key: string]: number };
    _answerFailureAlertsSent?: Set<string>;
  }
}

// NON-BLOCKING vehicle interest insertion with debouncing
// CRITICAL: sessionId is passed in synchronously to avoid race conditions
const insertVehicleInterestToDatabase = async (interestData: any, summary: string, capturedSessionId: string) => {
  // Use setTimeout to make this completely non-blocking for UI rendering
  setTimeout(async () => {
    try {
      // Use the pre-captured sessionId passed in synchronously (avoids "unknown" from async lookup)
      const sessionId = capturedSessionId;
      const dealerIdResult = await getDealerID();
      
      if (!dealerIdResult) {
        console.error('🚗 No dealer ID found, cannot insert vehicle interest');
        return;
      }
      
      const vehicleText = interestData.vehicle?.make && interestData.vehicle?.model 
        ? `${interestData.vehicle.make} ${interestData.vehicle.model}` 
        : summary;

      // Use a debounce key to prevent rapid duplicate insertions
      const debounceKey = `${sessionId}-${dealerIdResult}-${vehicleText}`;
      const now = Date.now();
      
      // Check if we recently processed this exact combination (last 5 seconds)
      const lastProcessed = window._vehicleInterestDebounce?.[debounceKey] || 0;
      if (now - lastProcessed < 5000) {
        logger.log('🚗 Vehicle interest debounced, skipping rapid duplicate');
        return;
      }
      
      // Update debounce tracking
      if (!window._vehicleInterestDebounce) window._vehicleInterestDebounce = {};
      window._vehicleInterestDebounce[debounceKey] = now;
      
      const dealerId = dealerIdResult;
      const vehicleString = summary || 'Vehicle Interest';
      const siteUrl = interestData.vehicle?.VDP_URL || interestData.vehicle?.vdp_url || null;

      // Use base supabase client to avoid dealer context headers interfering with edge functions
      const { supabase } = await import('@/integrations/supabase/client');
      
      // Insert via Edge Function to bypass RLS headers
      const { data: insertResult, error } = await supabase.functions.invoke('vehicle-interest', {
        body: {
          action: 'insert',
          session_id: sessionId,
          dealer_id: dealerId,
          vehicle: vehicleString,
          source: 'AI',
          site_url: siteUrl || undefined,
        }
      });

      const data = (insertResult as any)?.data ?? null;

      if (error) {
        // If it's a duplicate key error, that's fine - just log and continue
        if (error.code === '23505') {
          logger.log('🚗 Vehicle interest already exists (duplicate key), skipping');
          return;
        }
        console.error('🚗 Error inserting vehicle interest:', error);
        return;
      }
      
      if (data) {
        logger.log('🚗 Successfully inserted vehicle interest to database:', data);
        
        // Dispatch event asynchronously
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('vehicleInterestAdded', { 
            detail: { data } 
          }));
        }, 0);
      }
      
    } catch (error) {
      console.error('🚗 Background vehicle interest insertion failed:', error);
    }
  }, 0); // Run asynchronously after current call stack
};

// NON-BLOCKING answer data handling
const handleAnswerData = async (fullOpenAIResponse: any, capturedSessionId?: string) => {
  // Run all database operations in background to avoid blocking UI
  setTimeout(async () => {
    try {
      logger.log('🎯 Processing Answer data (background):', fullOpenAIResponse);
      
      const success = fullOpenAIResponse.Answer?.Success;
      const aiQuestion = fullOpenAIResponse.Answer?.Question; // AI's rephrased version
      // Send the entire OpenAI response as JSON so we can see Reply_Message, intro, closing, etc.
      const answerJson = JSON.stringify(fullOpenAIResponse, null, 2);
      
      if (typeof success !== 'boolean' || !aiQuestion) {
        logger.log('🎯 Invalid Answer data format, skipping');
        return;
      }
      
      // Get session data (best-effort) BUT prefer the captured sessionId from the parser call stack
      const session = await sessionManager.getCurrentSessionAsync();
      const sessionId = capturedSessionId || session?.sessionId || 'unknown';
      
      // CRITICAL FIX: Get the ACTUAL user message from sessionStorage (saved synchronously in useChat.ts)
      // This is more reliable than async session manager because it's saved immediately when user sends message
      let actualUserQuestion = aiQuestion; // Default to AI version
      try {
        const storedMessages = sessionStorage.getItem('d2c_chat_messages');
        if (storedMessages) {
          const messages = JSON.parse(storedMessages);
          // Find the last user message (messages are stored with role: 'user' or 'assistant')
          const lastUserMsg = [...messages].reverse().find((m: any) => m.role === 'user');
          if (lastUserMsg?.content) {
            actualUserQuestion = lastUserMsg.content;
            logger.log('🎯 Found actual user question from sessionStorage:', actualUserQuestion.substring(0, 50));
          }
        }
      } catch (e) {
        logger.error('🎯 Error reading user message from sessionStorage:', e);
      }
      
      const dealerIdResult = await getDealerID();
      
      if (!dealerIdResult) {
        console.error('🎯 No dealer ID found, cannot store answer data');
        return;
      }
      
      // localStorage-only mode - no database updates needed
      const updateSuccess = true;
      
      if (updateSuccess) {
        logger.log('🎯 Successfully stored answer data in database');
      } else {
        logger.error('🎯 Failed to store answer data in database');
      }
      
      // Send email alert if answer was unsuccessful - also in background
      if (success === false) {
        // Create a unique key to prevent duplicate alerts for the same question/session
        // IMPORTANT: Use aiQuestion (always available) for dedup key, not actualUserQuestion
        // This ensures consistent deduplication regardless of timing issues
        const alertKey = `${sessionId}-${aiQuestion.substring(0, 50)}`; // Use first 50 chars to create unique key
        
        // SECOND LAYER: Dealer-level deduplication with time window to prevent duplicates across sessions
        // This prevents alerts when user opens multiple tabs or refreshes
        const dealerAlertKey = `${dealerIdResult}-${aiQuestion.substring(0, 50).toLowerCase().trim()}`;
        const dealerAlertStorageKey = 'answer_failure_dealer_alerts';
        const ALERT_COOLDOWN_MS = 5 * 60 * 1000; // 5 minute cooldown per dealer/question combo
        
        try {
          const storedDealerAlerts = localStorage.getItem(dealerAlertStorageKey);
          const dealerAlerts: Record<string, number> = storedDealerAlerts ? JSON.parse(storedDealerAlerts) : {};
          
          const lastAlertTime = dealerAlerts[dealerAlertKey];
          if (lastAlertTime && (Date.now() - lastAlertTime) < ALERT_COOLDOWN_MS) {
            logger.log('🎯 Dealer-level duplicate detected, skipping alert (cooldown active)');
            return;
          }
        } catch (e) {
          logger.error('🎯 Error reading dealer alerts from localStorage:', e);
        }
        
        // Use localStorage for persistent tracking across page reloads
        const alertStorageKey = 'answer_failure_alerts_sent';
        let sentAlerts: string[] = [];
        
        try {
          const stored = localStorage.getItem(alertStorageKey);
          sentAlerts = stored ? JSON.parse(stored) : [];
        } catch (e) {
          logger.error('🎯 Error reading sent alerts from localStorage:', e);
          sentAlerts = [];
        }
        
        // Check if we already sent an alert for this specific question/session combination
        if (sentAlerts.includes(alertKey)) {
          logger.log('🎯 Answer failure alert already sent for this question/session, skipping duplicate');
          return;
        }
        
        // Only send alerts for recent failures (within last 24 hours) to prevent old data from triggering alerts
        const currentTime = new Date().getTime();
        const oneDayAgo = currentTime - (24 * 60 * 60 * 1000);
        
        // Extract timestamp from session if possible, otherwise assume it's recent
        const sessionCreatedAt = session?.createdAt || currentTime;
        const sessionTime = typeof sessionCreatedAt === 'string' ? new Date(sessionCreatedAt).getTime() : sessionCreatedAt;
        
        if (sessionTime < oneDayAgo) {
          logger.log('🎯 Skipping answer failure alert for old session (> 24 hours old)');
          return;
        }
        
        logger.log('🎯 Answer marked as unsuccessful, sending email alert');
        
        setTimeout(async () => {
          try {
            // Use base supabase client to avoid dealer context headers interfering with edge functions
            const { supabase } = await import('@/integrations/supabase/client');
            
            const openaiSessionId = sessionStorage.getItem('openai_response_id_tab') || localStorage.getItem('openai_response_id_direct') || null;
            const { error: emailError } = await supabase.functions.invoke('send-answer-failure-alert', {
              body: {
                question: actualUserQuestion, // Send the ACTUAL user message, not AI's rephrased version
                answerJson: answerJson,
                dealerId: dealerIdResult.toString(),
                sessionId: sessionId,
                openaiSessionId: openaiSessionId,
                timestamp: new Date().toISOString()
              }
            });
            
            if (emailError) {
              console.error('🎯 Error sending answer failure alert:', emailError);
            } else {
              logger.log('🎯 Answer failure alert sent successfully');
              // Mark this alert as sent to prevent duplicates
              sentAlerts.push(alertKey);
              
              // Keep only the last 100 alerts to prevent localStorage from growing too large
              if (sentAlerts.length > 100) {
                sentAlerts = sentAlerts.slice(-100);
              }
              
              try {
                localStorage.setItem(alertStorageKey, JSON.stringify(sentAlerts));
                
                // Also update dealer-level deduplication timestamp
                const storedDealerAlerts = localStorage.getItem(dealerAlertStorageKey);
                const dealerAlerts: Record<string, number> = storedDealerAlerts ? JSON.parse(storedDealerAlerts) : {};
                dealerAlerts[dealerAlertKey] = Date.now();
                
                // Clean up old entries (keep only last 50)
                const entries = Object.entries(dealerAlerts);
                if (entries.length > 50) {
                  const sorted = entries.sort((a, b) => b[1] - a[1]);
                  const cleaned = Object.fromEntries(sorted.slice(0, 50));
                  localStorage.setItem(dealerAlertStorageKey, JSON.stringify(cleaned));
                } else {
                  localStorage.setItem(dealerAlertStorageKey, JSON.stringify(dealerAlerts));
                }
              } catch (e) {
                logger.error('🎯 Error saving sent alerts to localStorage:', e);
              }
            }
          } catch (emailError) {
            console.error('🎯 Exception sending answer failure alert:', emailError);
          }
        }, 0);
      }
      
    } catch (error) {
      console.error('🎯 Error handling answer data:', error);
    }
  }, 0); // Run asynchronously
};

export const parseJSONMessage = async (jsonData: any, options?: { sessionId?: string; skipIntroAndClosing?: boolean }): Promise<MessagePart[]> => {
  const parts: MessagePart[] = [];
  
  // CRITICAL: Capture sessionId synchronously at entry point to avoid race conditions
  // If not provided, get it NOW before any async operations
  const capturedSessionId = options?.sessionId || sessionManager.getCurrentSession()?.sessionId || 'unknown';
  const skipIntroAndClosing = options?.skipIntroAndClosing ?? false;
  
  logger.log('🔍 parseJSONMessage called with keys:', Object.keys(jsonData));
  logger.log('🔍 Captured sessionId:', capturedSessionId);
  logger.log('🔍 skipIntroAndClosing:', skipIntroAndClosing);
  logger.log('🔍 Has Reply_Message:', !!jsonData.Reply_Message);
  logger.log('🔍 Has root Closing:', !!jsonData.Closing);
  logger.log('🔍 Has root Proposed_Link:', !!jsonData.Proposed_Link);
  
  // CRITICAL: Validate Content.Inventory to catch OpenAI duplicate key bugs
  if (jsonData.Reply_Message?.Content) {
    const inventory = jsonData.Reply_Message.Content.Inventory;
    if (Array.isArray(inventory)) {
      const hasVehicles = inventory.length > 0 && inventory[0]?.Main_Picture_URL;
      const isEmpty = inventory.length === 0;
      logger.log('🚗 Content.Inventory check:', { 
        length: inventory.length, 
        hasVehicles, 
        isEmpty,
        firstItem: inventory[0] ? Object.keys(inventory[0]).slice(0, 5) : 'none'
      });
      
      // If Inventory is empty but we expect vehicles, log a warning
      if (isEmpty) {
        logger.warn('⚠️ Content.Inventory is empty - possible duplicate key issue in OpenAI response');
      }
    }
  }
  
  // HANDLE ANSWER OBJECT FOR SUCCESS/FAILURE TRACKING
  if (jsonData.Answer) {
    logger.log('🎯 FOUND Answer object:', jsonData.Answer);
    handleAnswerData(jsonData, capturedSessionId);
  }
  
  // EXTRACT DEPARTMENT FROM VEHICLE_COUNT CATEGORY (e.g., "Used Vehicles" -> "Used")
  let departmentFromVehicleCount: string | null = null;
  const vehicleCount = jsonData.Reply_Message?.Content?.Vehicle_Count || jsonData.Content?.Vehicle_Count;
  if (vehicleCount && Array.isArray(vehicleCount) && vehicleCount.length > 0) {
    const category = vehicleCount[0]?.Category || vehicleCount[0]?.category || '';
    if (category.toLowerCase().includes('used')) {
      departmentFromVehicleCount = 'Used';
      logger.log('🎯 DEPARTMENT EXTRACTED from Vehicle_Count.Category: Used');
    } else if (category.toLowerCase().includes('new')) {
      departmentFromVehicleCount = 'New';
      logger.log('🎯 DEPARTMENT EXTRACTED from Vehicle_Count.Category: New');
    }
  }

  // STORE INTEREST DATA IF PRESENT - OPTIMIZED NON-BLOCKING
  if (jsonData.Interest || departmentFromVehicleCount) {
    const interest = jsonData.Interest || {};
    logger.log('🔥 STORING Interest data from messageParser:', interest);
    logger.log('🔥 Interest Summary from AI:', interest.summary);
    logger.log('🔥 Department from Vehicle_Count:', departmentFromVehicleCount);
    
    // Use try-catch for localStorage to handle quota exceeded errors
    try {
      // Since data is now encrypted, we'll start with empty data and let the store function handle merging
      // The secure storage system will handle getting existing data properly
      const existingData = { vehicle: {} };
      logger.log('🔥 Starting with empty data for merging (secure storage will handle existing data):', existingData);
      
      const existingVehicle = existingData.vehicle || {};
      const newVehicle = interest.vehicle || {};
      
      // Only update vehicle fields that have actual values, preserve existing detailed data
      const mergedVehicle = {
        ...existingVehicle,
        // Only update fields that have non-empty values from AI response
        ...(newVehicle.make && newVehicle.make.trim() ? { make: newVehicle.make } : {}),
        ...(newVehicle.model && newVehicle.model.trim() ? { model: newVehicle.model } : {}),
        ...(newVehicle.version && newVehicle.version.trim() ? { version: newVehicle.version } : {}),
        ...(newVehicle.year && newVehicle.year.toString().trim() ? { year: newVehicle.year } : {}),
        ...(newVehicle.color && newVehicle.color.trim() ? { color: newVehicle.color } : {}),
        ...(newVehicle.vin && newVehicle.vin.trim() ? { vin: newVehicle.vin } : {}),
        ...(newVehicle.VIN && newVehicle.VIN.trim() ? { VIN: newVehicle.VIN } : {}),
        ...(newVehicle.stock && newVehicle.stock.trim() ? { stock: newVehicle.stock } : {}),
        ...(newVehicle.status && newVehicle.status.trim() ? { status: newVehicle.status } : {})
      };
      
      // CRITICAL: Set department from Vehicle_Count.Category if not already set
      const effectiveDepartment = interest.department || departmentFromVehicleCount;
      
      const mergedInterestData = {
        ...interest,
        department: effectiveDepartment,
        vehicle: mergedVehicle
      };
      
      logger.log('🔥 Merged Interest data with department:', effectiveDepartment);
      storeInterestData(mergedInterestData);
      
      // Note: Storage verification now handled by secure storage system
      logger.log('🔥 Interest data stored using secure storage system');
      
      // Insert vehicle interest into database if Interest summary is present (check both cases)
      // CRITICAL: Must null-check jsonData.Interest since this block runs when departmentFromVehicleCount is set but Interest may be undefined
      const summary = jsonData.Interest?.summary || jsonData.Interest?.Summary;
      if (summary) {
        logger.log('🔥 Interest summary found, scheduling database insertion with sessionId:', capturedSessionId);
        // Schedule database insertion to run in background - don't await or block UI
        // Pass capturedSessionId to avoid race condition with async session lookup
        insertVehicleInterestToDatabase(mergedInterestData, summary, capturedSessionId);
      }
    } catch (storageError) {
      console.error('🔥 Error handling interest data:', storageError);
      // Still try to insert to database even if localStorage fails
      // CRITICAL: Must null-check jsonData.Interest since it may be undefined
      const summary = jsonData.Interest?.summary || jsonData.Interest?.Summary;
      if (summary && jsonData.Interest) {
        insertVehicleInterestToDatabase(jsonData.Interest, summary, capturedSessionId);
      }
    }
  }
  
  // ENHANCED: Check for Form objects and trigger form logic
  if (jsonData.Form || jsonData.FormTitle) {
    logger.log('*** FOUND Form object in JSON:', jsonData.Form, 'FormTitle:', jsonData.FormTitle);
    // The form logic will be handled by useChatFormLogic, but we note it here for completeness
  }
  
  // Check for structured message types
  const structuredResult = parseStructuredMessageTypes(jsonData);
  if (structuredResult.length > 0) {
    parts.push(...structuredResult);
  }
  
  // Check for reply message structure
  if (jsonData.Reply_Message) {
    logger.log('*** FOUND Reply_Message, calling parseReplyMessage (skipIntroAndClosing:', skipIntroAndClosing, ')');
    try {
      const replyParts = await parseReplyMessage(jsonData, { skipIntroAndClosing });
      logger.log('✅ parseReplyMessage returned:', replyParts.length, 'parts');
      if (replyParts.length > 0) {
        parts.push(...replyParts);
      } else {
        logger.warn('⚠️ parseReplyMessage returned 0 parts - will use fallback logic');
      }
    } catch (error) {
      logger.error('❌ Error in parseReplyMessage:', error);
      // Don't fallback to raw JSON - let the subsequent fallback logic handle it properly
    }
  }
  
  // Check for root-level content
  const rootLevelResult = parseRootLevelContent(jsonData);
  if (rootLevelResult.length > 0) {
    parts.push(...rootLevelResult);
  }
  
  // Handle CARFAX processing
  const carfaxResult = parseCarfaxContent(jsonData);
  if (carfaxResult.length > 0) {
    parts.push(...carfaxResult);
  }
  
  logger.log('🏁 parseJSONMessage final parts count:', parts.length);
  logger.log('🔍 parseJSONMessage final parts types:', parts.map(p => p.type).join(', '));
  if (parts.length === 0) {
    logger.warn('⚠️ No parts generated from JSON - attempting fallback rendering');
    logger.log('🔍 parseJSONMessage - jsonData keys:', Object.keys(jsonData));
    logger.log('🔍 parseJSONMessage - Has Reply_Message?', !!jsonData.Reply_Message);
    logger.log('🔍 parseJSONMessage - Reply_Message keys:', jsonData.Reply_Message ? Object.keys(jsonData.Reply_Message) : 'N/A');
    logger.log('🔍 parseJSONMessage - skipIntroAndClosing:', skipIntroAndClosing);
    
    // If we have a Reply_Message but no parts, something went wrong - show formatted text
    if (jsonData.Reply_Message) {
      // Add intro if present AND we're not skipping intro/closing (already streamed)
      if (jsonData.Reply_Message.Intro && !skipIntroAndClosing) {
        parts.push({ type: 'text', content: jsonData.Reply_Message.Intro });
      }
      
      // Check for inventory and render if present
      const inventory = jsonData.Reply_Message.Content?.Inventory;
      if (inventory && Array.isArray(inventory) && inventory.length > 0) {
        // Hydrate vehicles from localStorage using VIN
        const hydratedInventory = await processInventoryHydration(inventory);
        
        // Only show if inventory has actual vehicle data (not empty objects)
        const hasRealVehicles = hydratedInventory.some(v => v.Main_Picture_URL || v.Make || v.Model);
        if (hasRealVehicles) {
          logger.log('✅ Fallback: Found', hydratedInventory.length, 'vehicles in Content.Inventory');
          // Sort inventory by price in ascending order
          const sortedInventory = [...hydratedInventory].sort((a, b) => {
            const priceA = a.Price ?? Infinity;
            const priceB = b.Price ?? Infinity;
            return priceA - priceB;
          });
          parts.push({ type: 'vehicles_list', content: sortedInventory });
        } else {
          logger.warn('⚠️ Inventory array exists but contains no valid vehicles');
        }
      } else {
        logger.warn('⚠️ No inventory found or inventory is empty');
      }
      
      // Add closing if present AND we're not skipping intro/closing (already streamed)
      if ((jsonData.Closing || jsonData.Reply_Message.Closing) && !skipIntroAndClosing) {
        parts.push({ type: 'text', content: jsonData.Closing || jsonData.Reply_Message.Closing });
      }
      
      // CRITICAL: When skipIntroAndClosing is true and we have no displayable parts,
      // return empty array instead of error message - the streamed intro/closing is enough
      if (parts.length === 0 && skipIntroAndClosing) {
        logger.log('✅ skipIntroAndClosing=true with no additional content - returning empty (streaming handled display)');
        return parts; // Return empty - streaming already showed intro/closing
      }
      
      // If still no parts after fallback, show error message instead of raw JSON
      if (parts.length === 0) {
        logger.error('❌ Fallback failed - no displayable content found');
        parts.push({ 
          type: 'text', 
          content: 'I apologize, but I encountered an error displaying this response. Please try rephrasing your question.' 
        });
      }
    } else {
      // No Reply_Message at all - show generic error (unless skipIntroAndClosing, then streaming handled it)
      if (!skipIntroAndClosing) {
        parts.push({ 
          type: 'text', 
          content: 'I apologize, but I encountered an error processing this response. Please try again.' 
        });
      }
    }
  }
  
  return parts;
};

const parseStructuredMessageTypes = (jsonData: any): MessagePart[] => {
  const parts: MessagePart[] = [];
  
  // Check for new inventory_count format from vehicleDataFormatter
  if (jsonData.type === 'inventory_count' && jsonData.data) {
    logger.log('*** FOUND inventory_count type from formatter:', jsonData.data);
    parts.push({ type: 'inventory_count', content: jsonData.data });
  }
  
  // Check for new promotions_list format from vehicleDataFormatter
  if (jsonData.type === 'promotions_list' && jsonData.data) {
    logger.log('*** FOUND promotions_list type from formatter:', jsonData.data);
    parts.push({ type: 'promotions_list', content: jsonData.data });
  }
  
  // Check for promotions content
  if (jsonData.Content?.Promotions && Array.isArray(jsonData.Content.Promotions)) {
    logger.log('*** FOUND Promotions in Content:', jsonData.Content.Promotions);
    parts.push({ type: 'promotions_list', content: jsonData.Content.Promotions });
  }
  
  // Handle special structured message types first
  if (jsonData.type === 'carfax_report') {
    logger.log('*** FOUND carfax_report type');
    parts.push({ type: 'carfax_report', content: jsonData });
  }
  
  return parts;
};

const parseRootLevelContent = (jsonData: any): MessagePart[] => {
  const parts: MessagePart[] = [];
  
  // CHECK FOR REPLY_OPTIONS AT ROOT LEVEL (both cases - array and object)
  if (jsonData.reply_options && Array.isArray(jsonData.reply_options)) {
    parts.push({ type: 'reply_options', content: { reply_options: jsonData.reply_options } });
  }
  
  if (jsonData.Reply_Options || jsonData.Content?.Reply_Options) {
    const replyOptions = jsonData.Reply_Options || jsonData.Content?.Reply_Options;
    const replyOptionsContent = { Reply_Options: replyOptions };
    parts.push({ type: 'reply_options', content: replyOptionsContent });
  }
  
  // Check for root-level similar_vehicles or Proposed_Link (only if no Reply_Message)
  if (jsonData.similar_vehicles && !jsonData.Reply_Message) {
    logger.log('*** FOUND SIMILAR_VEHICLES AT ROOT LEVEL:', jsonData.similar_vehicles);
    const hasVehicles = jsonData.Content?.Inventory && Array.isArray(jsonData.Content.Inventory) && jsonData.Content.Inventory.length > 0;
    parts.push({ 
      type: 'similar_vehicles', 
      content: { similar_vehicles: jsonData.similar_vehicles, hasVehicles } 
    });
  }
  
  if (jsonData.Proposed_Link && !jsonData.Reply_Message) {
    logger.log('*** FOUND PROPOSED_LINK AT ROOT LEVEL:', jsonData.Proposed_Link);
    const hasVehicles = jsonData.Content?.Inventory && Array.isArray(jsonData.Content.Inventory) && jsonData.Content.Inventory.length > 0;
    parts.push({ 
      type: 'similar_vehicles', 
      content: { similar_vehicles: jsonData.Proposed_Link, hasVehicles } 
    });
  }
  
  // Check if it's a vehicle object
  if (hasVehicleProperties(jsonData)) {
    parts.push({ type: 'vehicle', content: jsonData });
  }
  
  // Check if it's a service form object
  if (jsonData.form === 'Service') {
    parts.push({ type: 'service_form', content: jsonData });
  }
  
  // Check if it's a close form object
  if (jsonData.form === 'Close') {
    parts.push({ type: 'close_form', content: jsonData });
  }
  
  // Handle standalone Window_Sticker at root level
  if (jsonData.Window_Sticker && !jsonData.Reply_Message) {
    const windowStickerLink = `<a href="${jsonData.Window_Sticker}" target="_blank" rel="noopener noreferrer" class="inline-block bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors duration-200 text-sm font-semibold no-underline">View the Window Sticker</a>`;
    parts.push({ type: 'text', content: windowStickerLink });
  }
  
  return parts;
};

const parseCarfaxContent = (jsonData: any): MessagePart[] => {
  const parts: MessagePart[] = [];
  
  // Handle CARFAX_URL - both standalone and with Reply_Message, check both root and Content
  const carfaxUrl = jsonData.CARFAX_URL || jsonData.Content?.CARFAX_URL;
  if (carfaxUrl) {
    logger.log('*** FOUND CARFAX_URL, processing carfax tracking logic');
    
    // Get vehicle data from stored interest or from the current response
    // Note: Since data is encrypted now, we'll get vehicle data from the current response
    const vehicleData = jsonData.Interest?.vehicle || {};
    const vin = vehicleData.VIN || vehicleData.vin || '';
    
    logger.log('*** CARFAX - Vehicle data:', vehicleData);
    logger.log('*** CARFAX - VIN:', vin);
    logger.log('*** CARFAX - URL:', carfaxUrl);
    
    if (vin) {
      // Track carfax request
      addCarfaxRequest(vin);
      
      // Check if we should show form or report
      if (shouldShowCarfaxForm(vin)) {
        logger.log('*** CARFAX - Should show form for VIN:', vin);
        // Return carfax form if no Reply_Message
        if (!jsonData.Reply_Message) {
          parts.push({ 
            type: 'service_form', 
            content: { 
              form: 'Carfax',
              FormTitle: 'Request Carfax Report',
              vehicleVin: vin
            } 
          });
        }
      } else if (shouldShowCarfaxReport(vin)) {
        logger.log('*** CARFAX - Should show report for VIN:', vin);
        // Track that carfax was sent
        updateCarfaxSent(vin);
        
        // Return carfax report if no Reply_Message
        if (!jsonData.Reply_Message) {
          parts.push({ 
            type: 'carfax_report', 
            content: { 
              vehicleData: vehicleData,
              carfaxUrl: carfaxUrl
            } 
          });
        }
      } else {
        logger.log('*** CARFAX - Should not show anything for VIN:', vin, '(form submitted but outside 30-day window)');
      }
    }
  }
  
  return parts;
};